import BaseService from './baseService';
import { GridFilter, LinkFilter, UploadInputParams, DownloadInputsParams, SaveScenarioPayload } from 'store/scenarioMgmt/types';

export default class extends BaseService {
    constructor() {
        super('scenario');
    }

    /**
    * Gets Scenario
    * @param {number} scenarioId - scenario Id
    * @returns Scenario object
    */
    getScenario = (scenarioId: number) => this.httpGetRequest(`${scenarioId}`);

    /**
    * Gets list of Scenario items
    * @param {Object<GridFilter>} filter - default filter
    * @param {string[]} filter.keywords - search keywords
    * @param {number} filter.pageNumber - page number
    * @param {number} filter.pageSize - page size
    * @param {string} filter.orderByColumn - column name for sorting;
    * @param {number} filter.sortingDirection - 0 or 1
    * @param {number[]} filter.scenarioTypes - RA(1), non-RA(0), both[0, 1]
    * @param {number} filter.taskId - task id
    * @returns list of Scenario Statuses
    */
    getScenarios = (filter: Partial<GridFilter>) => this.httpPostRequest('list', filter);

    /**
    * Update(save) Scenario
    * @param {Object<SaveScenarioPayload>} scenario - Scenario
    * @returns scenario Id
    */
    putScenario = (scenario: SaveScenarioPayload) => this.httpPutRequest('', scenario);

    /**
    * Delete selected scenario
    * @param {number[]} scenarioIds - scenario ids
    * @returns void
    */
    deleteScenarios = (scenarioIds: number[]) => this.httpPostRequest("delete", scenarioIds);

    /**
    * Run PSO for selected scenarios
    * @param {number[]} scenarioIds - scenario ids
    * @returns void
    */
    runScenarios = (scenarioIds: number[]) => this.httpPostRequest("run", scenarioIds);

    /**
    * Validate selected scenarios
    * @param {number[]} scenarioIds - scenario ids
    * @returns void
    */
    validateScenarios = (scenarioIds: number[]) => this.httpPostRequest("validate", scenarioIds);

    /**
    * Stops selected scenarios
    * @param {number[]} scenarioIds - scenario ids
    * @returns void
    */
    stopScenarios = (scenarioIds: number[]) => this.httpPostRequest("stop", scenarioIds);

    /**
    * Restart failed cases among selected cases
    * @param {number[]} scenarioIds - scenario ids
    * @returns void
    */
    restartFailedCases = (scenarioIds: number[]) => this.httpPostRequest(`restart`, scenarioIds);

    /**
    * Сopies scenario inputs after copying the case
    * @param {number} sourceId - id from which to copy
    * @param {number} destinationId - id to which copy
    * @returns void
    */
    copyInputs = ({ sourceId, destinationId }: { sourceId: number; destinationId: number; }) => this.httpPostRequest("copyInputs", { sourceId, destinationId });

    /**
    * Gets inputs
    * @param {number} scenarioId - scenario id
    * @returns list of input files
    */
    getInputs = (scenarioId: number) => this.httpGetRequest(`getInputs/${scenarioId}`);

    /**
    * Deletes inputs
    * @param {number} scenarioId - scenario id
    * @param {string[]} files - file names
    * @returns void
    */
    deleteInputs = ({ scenarioId, files }: { scenarioId: number; files: string[]; }) => this.httpPostRequest(`deleteInputs`, { files, scenarioId });

    /**
    * Checks Instance
    * @param {number[]} scenarioIds - scenario ids
    * @returns Object { maxInstances: number; instanceCount: number }
    */
    checkInstanceCapacity = (scenarioIds: number[]) => this.httpPostRequest(`checkInstanceCapacity`, scenarioIds);

    /**
    * Gets Links
    * @param {Object<LinkFilter>} filter - scenario id
    * @returns list of links
    */
    getLinks = (filter: Partial<LinkFilter>) => this.httpPostRequest(`links`, filter);

    /**
    * Gets External Functions
    * @param {number} taskId - task Id
    * @returns list of functions
    */
    getExternalFunctions = (taskId: number) => this.httpGetRequest(`functions/${taskId}`);

    /**
    * Runs External Functions
    * @param {Object<RunExternalFunctionsParams>} request - request
    * @returns void
    */
    runExternalFunctions = (request: Partial<{
        id: number;
        name: string;
        allowUserPayload: boolean;
        description: string;
        payload?: string;
        scenarioIds?: number[];
        externalFunctionId: number
    }>) => this.httpPostRequest('functions', request);

    /**
    * Load Results selected cases to Athena
    * @param {number[]} scenarioIds - scenario Ids
    * @returns void
    */
    loadResults = (scenarioIds: number[]) => this.httpPostRequest('loadResults', scenarioIds);

    /**
    * Runs merge
    * @param {number[]} scenarioIds - scenario Ids
    * @returns void
    */
    runForceMerge = (scenarioIds: number[]) => this.httpPostRequest('forceMerge', scenarioIds);

    /**
    * Upload Inputs to case
    * @param {number[]} scenarioId - scenario Id
    * @param {File[]} files - files in form data
    * @returns void
    */
    uploadInputs = ({ scenarioId, files }: UploadInputParams) => this.httpPostRequest(`uploadInputs/${scenarioId}`, files, { 'Content-Type': 'multipart/form-data' });

    /**
    * Download Inputs from case
    * @param {number[]} scenarioId - scenario Id
    * @param {string[]} files - file names
    * @returns urls for download file
    */
    downloadInputs = ({ scenarioId, files }: DownloadInputsParams) => this.httpPostRequest(`downloadInputs`, { files, scenarioId });
}
